import { useState } from 'react'

import { Popup } from '@fv/client-components'
import { parseDateRange } from '@fv/client-core'
import { fvDate } from '@fv/dates'
import {
  type DateRange,
  dateRangeNames,
  getDateRangeName,
  type PredefinedDateRange,
} from '@fv/models'
import { capitalizeFirstLetter } from '@fv/models/core'

import { CustomTimeframe } from '@/features/reports/CustomTimeframe'

import { type AdminMenuItem, AdminPopupMenu } from './AdminPopupMenu'

const formatRangeName = (range: PredefinedDateRange) =>
  capitalizeFirstLetter(getDateRangeName(range))

const formatDate = (date: Date) => fvDate(date).format('M/D/YY')

const defaultCustomRange = (): DateRange =>
  `${fvDate().subtract(1, 'week').valueOf()}-${fvDate().valueOf()}`

const getRangeError = (range: DateRange) => {
  const { start, end } = parseDateRange(range)
  if (!start || !end) return 'Both dates are required.'
  if (fvDate(start).isAfter(fvDate(end))) {
    return 'Start date must come before end date.'
  }
}

type Props = {
  value: DateRange
  onChange: (value: DateRange) => void
  ranges?: readonly PredefinedDateRange[]
  allowCustom?: boolean
}

export const AdminDateRangeMenu = ({
  value,
  onChange,
  ranges = dateRangeNames,
  allowCustom = false,
}: Props) => {
  const [customOpen, setCustomOpen] = useState(false)
  const [draft, setDraft] = useState<DateRange>(value)
  const { name } = parseDateRange(value)
  const { start, end } = parseDateRange(customOpen ? draft : value)
  const draftError = getRangeError(draft)
  const customSelected = name === 'custom'

  const handleCustomClick = () => {
    setDraft(name === 'custom' ? value : defaultCustomRange())
    setCustomOpen(true)
  }

  const handleCustomOpenChange = (open: boolean) => {
    if (open) {
      setDraft(value)
    } else if (!draftError && draft !== value) {
      onChange(draft)
    }
    setCustomOpen(open)
  }

  const items: AdminMenuItem[] = [
    ...ranges.map(range => ({
      key: range,
      label: formatRangeName(range),
      onClick: () => onChange(range),
      disabled: range === value,
    })),
    ...(allowCustom
      ? [{ key: 'custom', label: 'Custom', onClick: handleCustomClick }]
      : []),
  ]

  return (
    <div className="flex items-center">
      <AdminPopupMenu
        triggerClassName="btn btn-link btn-xs"
        trigger={
          <span>
            {customSelected || customOpen ? 'Custom' : formatRangeName(name)}
          </span>
        }
        value={customSelected ? 'custom' : value}
        items={items}
      />
      {(customSelected || customOpen) && start && end && (
        <Popup
          open={customOpen}
          onOpenChange={handleCustomOpenChange}
          trigger={
            <button type="button" className="btn btn-link btn-xs">
              {formatDate(start)} - {formatDate(end)}
            </button>
          }
          classNames={{
            positioner: 'z-[999]',
            popup:
              'bg-base-300 border border-base-300 rounded-box shadow-lg p-2 flex flex-col gap-1',
          }}
        >
          <div className="flex">
            <CustomTimeframe value={draft} onChange={setDraft} />
          </div>
          {draftError && (
            <span className="text-error text-xs">{draftError}</span>
          )}
        </Popup>
      )}
    </div>
  )
}
